;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();const IDEMPOTENCY_HEADER = 'Idempotency-Key';
const FLOW_HISTORY_STATE_KEY = '__nativeTeamsIdempotencyFlow';
const FLOW_STORAGE_PREFIX = 'native-teams:idempotency:';
const OPERATION_TTL_MS = 24 * 60 * 60 * 1000;
const GLOBAL_SUCCESS_DEDUPLICATION_WINDOW_MS = 3_000;
const PROCESSING_RETRY_ATTEMPTS = 3;

interface FlowMarker {
  scope: string;
  attemptId: string;
}

interface RegistryEntry {
  key: string;
  createdAt: number;
  reuseUntil?: number;
}

interface StoredRegistry {
  entries: Record<string, RegistryEntry>;
}

interface HistoryState {
  [FLOW_HISTORY_STATE_KEY]?: FlowMarker;
  [key: string]: object | string | number | boolean | null | undefined;
}

export interface PreparedIdempotentRequest {
  headers: Record<string, string>;
  idempotencyKey?: string;
  operationFingerprint?: string;
  flowAttemptId?: string;
  successDeduplicationWindowMs?: number;
  isManaged: boolean;
}

export interface PrepareIdempotentRequestOptions {
  url: string;
  method?: string;
  body?: RequestInit['body'];
  headers?: HeadersInit;
  useActiveFlow?: boolean;
  successDeduplicationWindowMs?: number;
}

export interface SettleIdempotentRequestOptions {
  hasResponse: boolean;
  status?: number;
}

export interface ServerIdempotencyHeadersOptions {
  method?: string;
  headers?: HeadersInit;
  inboundHeaders?: HeadersInit;
}

let activeFlowMarker: FlowMarker | null = null;
const pendingOperations = new Map<string, RegistryEntry>();

function getMemoryRegistryKey(fingerprint: string, attemptId?: string): string {
  return `${attemptId ?? 'global'}:${fingerprint}`;
}

function isIdempotentMethod(method?: string): method is string {
  return ['POST', 'PUT', 'PATCH'].includes(method?.toUpperCase() ?? '');
}

function isDefinitiveClientError(status?: number): boolean {
  if (status === undefined || status < 400 || status >= 500) return false;
  return ![408, 409, 425, 429].includes(status);
}

function isSuccessfulResponse(status?: number): boolean {
  return status !== undefined && status >= 200 && status < 300;
}

function isRegistryEntryReusable(entry: RegistryEntry): boolean {
  const now = Date.now();
  return (
    entry.createdAt >= now - OPERATION_TTL_MS &&
    (entry.reuseUntil === undefined || entry.reuseUntil > now)
  );
}

function headersToRecord(headers: Headers): Record<string, string> {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

function getHeader(headers: HeadersInit | undefined, name: string): string | undefined {
  if (!headers) return undefined;
  return new Headers(headers).get(name)?.trim() || undefined;
}

function bytesToHex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function hashValue(value: string | ArrayBuffer): Promise<string> {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value;
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToHex(digest);
}

function normalizeUrl(url: string): string {
  try {
    const base =
      typeof window === 'undefined' ? 'http://native-teams.local' : window.location.origin;
    const normalized = new URL(url, base);
    normalized.hash = '';
    normalized.searchParams.sort();
    return normalized.toString();
  } catch {
    return url;
  }
}

async function describeFormData(formData: FormData): Promise<string> {
  const parts: string[] = [];

  for (const [name, value] of formData.entries()) {
    if (typeof value === 'string') {
      parts.push(`${name.length}:${name}:text:${value.length}:${value}`);
      continue;
    }

    const contentHash = await hashValue(await value.arrayBuffer());
    parts.push(
      [
        `${name.length}:${name}`,
        'file',
        `${value.name.length}:${value.name}`,
        String(value.size),
        `${value.type.length}:${value.type}`,
        String(value.lastModified),
        contentHash,
      ].join(':')
    );
  }

  return parts.join('|');
}

async function fingerprintBody(body?: RequestInit['body']): Promise<string | null> {
  if (body === undefined || body === null) return hashValue('');
  if (typeof body === 'string') return hashValue(body);
  if (body instanceof URLSearchParams) return hashValue(body.toString());
  if (body instanceof FormData) return hashValue(await describeFormData(body));
  if (body instanceof ArrayBuffer) return hashValue(body);
  if (ArrayBuffer.isView(body)) {
    return hashValue(body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength));
  }
  if (body instanceof Blob) return hashValue(await body.arrayBuffer());

  return null;
}

async function createOperationFingerprint(
  method: string,
  url: string,
  body?: RequestInit['body']
): Promise<string | null> {
  const bodyFingerprint = await fingerprintBody(body);
  if (!bodyFingerprint) return null;
  return hashValue(`${method.toUpperCase()}:${normalizeUrl(url)}:${bodyFingerprint}`);
}

function getStorageKey(attemptId: string): string {
  return `${FLOW_STORAGE_PREFIX}${attemptId}`;
}

function pruneEntries(entries: Record<string, RegistryEntry>): Record<string, RegistryEntry> {
  return Object.fromEntries(
    Object.entries(entries).filter(([, entry]) => isRegistryEntryReusable(entry))
  );
}

function readFlowRegistry(attemptId: string): StoredRegistry {
  if (typeof window === 'undefined') return { entries: {} };

  try {
    const stored = window.sessionStorage.getItem(getStorageKey(attemptId));
    if (!stored) return { entries: {} };
    const parsed = JSON.parse(stored) as StoredRegistry;
    return { entries: pruneEntries(parsed.entries ?? {}) };
  } catch {
    return { entries: {} };
  }
}

function writeFlowRegistry(attemptId: string, registry: StoredRegistry): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(getStorageKey(attemptId), JSON.stringify(registry));
  } catch {
    // In-memory reuse still works when browser storage is unavailable.
  }
}

function getPendingOperation(
  fingerprint: string,
  flowMarker: FlowMarker | null
): RegistryEntry | undefined {
  if (!flowMarker) {
    const memoryKey = getMemoryRegistryKey(fingerprint);
    const registry = readFlowRegistry('global');
    const entry = registry.entries[fingerprint] ?? pendingOperations.get(memoryKey);
    if (entry && isRegistryEntryReusable(entry)) return entry;
    pendingOperations.delete(memoryKey);
    return undefined;
  }

  const registry = readFlowRegistry(flowMarker.attemptId);
  const memoryKey = getMemoryRegistryKey(fingerprint, flowMarker.attemptId);
  const entry = registry.entries[fingerprint] ?? pendingOperations.get(memoryKey);
  if (entry && isRegistryEntryReusable(entry)) {
    pendingOperations.set(memoryKey, entry);
    return entry;
  }
  pendingOperations.delete(memoryKey);
  return undefined;
}

function setPendingOperation(
  fingerprint: string,
  entry: RegistryEntry,
  flowMarker: FlowMarker | null
): void {
  pendingOperations.set(getMemoryRegistryKey(fingerprint, flowMarker?.attemptId), entry);

  const attemptId = flowMarker?.attemptId ?? 'global';
  const registry = readFlowRegistry(attemptId);
  registry.entries[fingerprint] = entry;
  writeFlowRegistry(attemptId, registry);
}

function removePendingOperation(
  fingerprint: string,
  flowAttemptId: string | undefined,
  idempotencyKey: string | undefined
): void {
  const attemptId = flowAttemptId ?? 'global';
  const memoryKey = getMemoryRegistryKey(fingerprint, flowAttemptId);
  const memoryEntry = pendingOperations.get(memoryKey);
  if (memoryEntry?.key === idempotencyKey) pendingOperations.delete(memoryKey);
  if (typeof window === 'undefined') return;

  const registry = readFlowRegistry(attemptId);
  if (registry.entries[fingerprint]?.key !== idempotencyKey) return;
  delete registry.entries[fingerprint];
  writeFlowRegistry(attemptId, registry);
}

function retainPendingOperationForDeduplication(
  fingerprint: string,
  idempotencyKey: string | undefined,
  deduplicationWindowMs: number
): void {
  const reuseUntil = Date.now() + deduplicationWindowMs;
  const memoryKey = getMemoryRegistryKey(fingerprint);
  const memoryEntry = pendingOperations.get(memoryKey);
  if (memoryEntry && memoryEntry.key === idempotencyKey) {
    pendingOperations.set(memoryKey, { ...memoryEntry, reuseUntil });
  }
  if (typeof window === 'undefined') return;

  const registry = readFlowRegistry('global');
  const registryEntry = registry.entries[fingerprint];
  if (!registryEntry || registryEntry.key !== idempotencyKey) return;
  registry.entries[fingerprint] = { ...registryEntry, reuseUntil };
  writeFlowRegistry('global', registry);
}

export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function getIdempotencyKey(headers?: HeadersInit): string | undefined {
  return getHeader(headers, IDEMPOTENCY_HEADER);
}

export async function prepareIdempotentRequest({
  url,
  method,
  body,
  headers,
  useActiveFlow = true,
  successDeduplicationWindowMs = GLOBAL_SUCCESS_DEDUPLICATION_WINDOW_MS,
}: PrepareIdempotentRequestOptions): Promise<PreparedIdempotentRequest> {
  const outgoingHeaders = new Headers(headers);
  if (!isIdempotentMethod(method)) {
    return { headers: headersToRecord(outgoingHeaders), isManaged: false };
  }

  const explicitKey = getIdempotencyKey(outgoingHeaders);
  if (explicitKey) {
    return {
      headers: headersToRecord(outgoingHeaders),
      idempotencyKey: explicitKey,
      isManaged: false,
    };
  }

  if (typeof window === 'undefined') {
    const idempotencyKey = createIdempotencyKey();
    outgoingHeaders.set(IDEMPOTENCY_HEADER, idempotencyKey);
    return {
      headers: headersToRecord(outgoingHeaders),
      idempotencyKey,
      isManaged: false,
    };
  }

  const operationFingerprint = await createOperationFingerprint(method, url, body);
  if (!operationFingerprint) {
    const idempotencyKey = createIdempotencyKey();
    outgoingHeaders.set(IDEMPOTENCY_HEADER, idempotencyKey);
    return {
      headers: headersToRecord(outgoingHeaders),
      idempotencyKey,
      isManaged: false,
    };
  }

  const flowMarker = useActiveFlow ? activeFlowMarker : null;
  const existing = getPendingOperation(operationFingerprint, flowMarker);
  const entry = existing ?? {
    key: createIdempotencyKey(),
    createdAt: Date.now(),
  };

  if (!existing) setPendingOperation(operationFingerprint, entry, flowMarker);
  outgoingHeaders.set(IDEMPOTENCY_HEADER, entry.key);

  return {
    headers: headersToRecord(outgoingHeaders),
    idempotencyKey: entry.key,
    operationFingerprint,
    flowAttemptId: flowMarker?.attemptId ?? 'global',
    successDeduplicationWindowMs,
    isManaged: true,
  };
}

export function settleIdempotentRequest(
  prepared: PreparedIdempotentRequest,
  { hasResponse, status }: SettleIdempotentRequestOptions
): void {
  if (!prepared.isManaged || !prepared.operationFingerprint) return;
  if (!hasResponse || status === 409) return;
  if (prepared.flowAttemptId !== 'global' && !isDefinitiveClientError(status)) return;

  if (
    prepared.flowAttemptId === 'global' &&
    isSuccessfulResponse(status) &&
    prepared.successDeduplicationWindowMs
  ) {
    retainPendingOperationForDeduplication(
      prepared.operationFingerprint,
      prepared.idempotencyKey,
      prepared.successDeduplicationWindowMs
    );
    return;
  }

  removePendingOperation(
    prepared.operationFingerprint,
    prepared.flowAttemptId,
    prepared.idempotencyKey
  );
}

export function createServerIdempotencyHeaders({
  method,
  headers,
  inboundHeaders,
}: ServerIdempotencyHeadersOptions): Record<string, string> {
  const outgoingHeaders = new Headers(headers);
  if (!isIdempotentMethod(method) || getIdempotencyKey(outgoingHeaders)) {
    return headersToRecord(outgoingHeaders);
  }

  const idempotencyKey = getIdempotencyKey(inboundHeaders) ?? createIdempotencyKey();
  outgoingHeaders.set(IDEMPOTENCY_HEADER, idempotencyKey);
  return headersToRecord(outgoingHeaders);
}

function getProcessingRetryDelay(response: Response): number {
  const retryAfter = Number(response.headers.get('retry-after'));
  if (!Number.isFinite(retryAfter) || retryAfter <= 0) return 250;
  return Math.min(retryAfter * 1000, 5000);
}

async function waitForProcessingRetry(response: Response): Promise<void> {
  await response.body?.cancel().catch(() => undefined);
  await new Promise((resolve) => setTimeout(resolve, getProcessingRetryDelay(response)));
}

export async function fetchPreparedIdempotentRequest(
  input: string | URL,
  init: RequestInit,
  preparedRequest: PreparedIdempotentRequest
): Promise<Response> {
  const request = {
    ...init,
    headers: preparedRequest.headers,
  };
  let response = await fetch(input, request);

  for (
    let attempt = 0;
    preparedRequest.isManaged && response.status === 409 && attempt < PROCESSING_RETRY_ATTEMPTS;
    attempt += 1
  ) {
    await waitForProcessingRetry(response);
    response = await fetch(input, request);
  }

  return response;
}

export async function fetchWithIdempotency(
  input: string | URL,
  init: RequestInit = {}
): Promise<Response> {
  const preparedRequest = await prepareIdempotentRequest({
    url: input.toString(),
    method: init.method,
    body: init.body,
    headers: init.headers,
    useActiveFlow: false,
    successDeduplicationWindowMs: 0,
  });

  try {
    const response = await fetchPreparedIdempotentRequest(input, init, preparedRequest);
    try {
      await response.clone().arrayBuffer();
      settleIdempotentRequest(preparedRequest, {
        hasResponse: true,
        status: response.status,
      });
    } catch {
      settleIdempotentRequest(preparedRequest, { hasResponse: false });
    }
    return response;
  } catch (error) {
    settleIdempotentRequest(preparedRequest, { hasResponse: false });
    throw error;
  }
}

/**
 * Idempotent request whose failure must not reject. Used for cleanup such as
 * clearing auth cookies, where a WebKit network error would otherwise become
 * an unhandled rejection at fire-and-forget call sites.
 */
export async function fetchIdempotentCleanup(
  input: string | URL,
  init: RequestInit = {}
): Promise<boolean> {
  try {
    const response = await fetchWithIdempotency(input, init);
    return response.ok;
  } catch {
    return false;
  }
}

export function startIdempotencyFlow(scope: string): FlowMarker | null {
  if (typeof window === 'undefined') return null;

  const historyState = (window.history.state ?? {}) as HistoryState;
  const existing = historyState[FLOW_HISTORY_STATE_KEY];
  const marker =
    existing?.scope === scope
      ? existing
      : {
          scope,
          attemptId: createIdempotencyKey(),
        };

  if (existing !== marker) {
    window.history.replaceState(
      {
        ...historyState,
        [FLOW_HISTORY_STATE_KEY]: marker,
      },
      ''
    );
  }

  activeFlowMarker = marker;
  const registry = readFlowRegistry(marker.attemptId);
  for (const [fingerprint, entry] of Object.entries(registry.entries)) {
    pendingOperations.set(getMemoryRegistryKey(fingerprint, marker.attemptId), entry);
  }

  return marker;
}

export function deactivateIdempotencyFlow(attemptId: string): void {
  if (activeFlowMarker?.attemptId !== attemptId) return;
  activeFlowMarker = null;
  pendingOperations.clear();
}

export function completeIdempotencyFlow(): void {
  if (typeof window === 'undefined' || !activeFlowMarker) return;

  window.sessionStorage.removeItem(getStorageKey(activeFlowMarker.attemptId));
  const historyState = { ...(window.history.state as HistoryState) };
  delete historyState[FLOW_HISTORY_STATE_KEY];
  window.history.replaceState(historyState, '');
  activeFlowMarker = null;
  pendingOperations.clear();
}

export function restartIdempotencyFlow(scope: string): FlowMarker | null {
  completeIdempotencyFlow();
  return startIdempotencyFlow(scope);
}
